import { CircleCircle } from '@calm/evexia-web-sdk';
import { MINUTE_MS } from '@calm/web-common/dist/time';
import createAiRecommendationsApi from '@rhg/libs/api/create-ai-recommendations-api';
import createAiRouterApi from '@rhg/libs/api/create-ai-router';
import useAuth from '@rhg/libs/auth0/use-auth';
import { logger } from '@rhg/libs/logger';
import {
    queryKeys,
    useQuery,
    useMutation,
    invalidateQueryKey,
} from '@rhg/libs/react-query';
import { useActiveCircle } from '../use-active-circle';

type ErrorProps = {
    status: number;
};

export function useAiRecommendations(shouldForceRefresh?: boolean) {
    const { activeCircle: circle } = useActiveCircle();
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const api = createAiRecommendationsApi(getAccessTokenSilently);

    const { data, ...otherProps } = useQuery({
        queryKey: [queryKeys.aiRecommendations, circle?.recipient.id],
        queryFn: async () => {
            const response =
                await api.getV1RecommendationsRecipientsRecipientIdAiRecommendations(
                    {
                        circleAuthorizationToken: circle!.authorizationJWT!,
                        recipientId: circle!.recipient.id,
                        ...(shouldForceRefresh ? { forceRefresh: true } : {}),
                    }
                );
            return response.data.recommendations;
        },
        enabled:
            isAuthenticated &&
            typeof circle?.authorizationJWT === 'string' &&
            typeof circle?.recipient.id === 'number',
        staleTime: 5 * MINUTE_MS, // 5 minutes
    });

    return {
        ...data,
        ...otherProps,
    };
}

interface SaveAiRecommendationsParams {
    conversationId: string;
    circle: CircleCircle;
}

export function useSaveAiRecommendations() {
    const { getAccessTokenSilently } = useAuth();
    const api = createAiRouterApi(getAccessTokenSilently);

    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async ({
            conversationId,
            circle,
        }: SaveAiRecommendationsParams) => {
            const response =
                await api.aiRouterAiRecommendationsGenerateAIRecommendationsV1({
                    circleAuthorizationToken: circle.authorizationJWT!,
                    aIRecommendationGenerateRequest: {
                        conversationId,
                    },
                });
            return response;
        },
        onSuccess: (_response, { circle }) => {
            invalidateQueryKey([
                queryKeys.aiRecommendations,
                circle.recipient.id,
            ]).catch((err: unknown) =>
                logger.error(
                    'useSaveAiRecommendations: failed to invalidate aiRecommendations query',
                    {},
                    err as Error
                )
            );
            // Invalidate both cache entries (with and without AI care plan)
            invalidateQueryKey([
                queryKeys.recommendations,
                circle.recipient.id,
                true,
            ]).catch((err: unknown) =>
                logger.error(
                    'useSaveAiRecommendations: failed to invalidate recommendations query',
                    {},
                    err as Error
                )
            );
            invalidateQueryKey([
                queryKeys.recommendations,
                circle.recipient.id,
                false,
            ]).catch((err: unknown) =>
                logger.error(
                    'useSaveAiRecommendations: failed to invalidate recommendations query',
                    {},
                    err as Error
                )
            );
        },
        retry: (failureCount, error: ErrorProps) => {
            if (error?.status === 502 && failureCount < 1) {
                failureCount++;
                return true;
            }
            return false;
        },
    });
    return {
        saveRecommendationData: mutate,
        saveRecommendationDataAsync: mutateAsync,
        ...otherProps,
    };
}
